import React, { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { format } from 'date-fns';
import { Shield, AlertCircle, RefreshCw } from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';
import { getConfig } from '../config.js';

export default function HodlerConviction() {
  const market = useMarketData();
  const { btc, chain, btcWeekly, fred, equityHistory } = market;

  const [hodlData, setHodlData] = useState({ outputVolume: [], totalSupply: [], status: 'loading', error: null });

  useEffect(() => {
    let cancelled = false;
    async function fetchHodl() {
      try {
        const { marketProxyUrl } = getConfig();
        const url = `${marketProxyUrl}${marketProxyUrl.includes('?') ? '&' : '?'}source=btc-hodl-metrics`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (data?.error_message) throw new Error(data.error_message);
        if (!cancelled) {
          setHodlData({
            outputVolume: (data.outputVolume || []).map(p => ({ ...p, date: parseDate(p.date) })).filter(p => p.date),
            totalSupply: (data.totalSupply || []).map(p => ({ ...p, date: parseDate(p.date) })).filter(p => p.date),
            status: 'live',
            error: null,
          });
        }
      } catch (e) {
        if (!cancelled) setHodlData(prev => ({ ...prev, status: 'error', error: e.message }));
      }
    }
    fetchHodl();
    return () => { cancelled = true; };
  }, []);

  const walletsData = chain.walletsMin1MSats || [];
  const btcSpot = btc.spot;
  const btcHistory = btc.history || [];
  const weeklyBars = btcWeekly.bars || [];

  const analysis = useMemo(() => {
    const { outputVolume, totalSupply } = hodlData;
    if (!outputVolume.length || !totalSupply.length) return null;

    // Velocity = output volume / total supply (lower = more HODLing)
    const supplyMap = new Map();
    for (const p of totalSupply) {
      if (p.date) supplyMap.set(`${p.date.getFullYear()}-${p.date.getMonth()}-${p.date.getDate()}`, p.value);
    }

    // Build velocity series (7-day moving average for smoothing)
    const velocitySeries = [];
    for (let i = 6; i < outputVolume.length; i++) {
      const window = outputVolume.slice(i - 6, i + 1);
      const avgOutput = window.reduce((s, p) => s + p.value, 0) / 7;
      const supply = totalSupply[Math.min(i, totalSupply.length - 1)]?.value || 20_000_000;
      const velocity = (avgOutput / supply) * 100;
      velocitySeries.push({ date: outputVolume[i].date, velocity, output: avgOutput });
    }

    // Latest metrics
    const latestVelocity = velocitySeries.length ? velocitySeries[velocitySeries.length - 1] : null;
    const velocity30dAgo = velocitySeries.length > 30 ? velocitySeries[velocitySeries.length - 31] : null;
    const velocity90dAgo = velocitySeries.length > 90 ? velocitySeries[velocitySeries.length - 91] : null;

    // Velocity trend (falling = more HODLing)
    const velocityTrend = latestVelocity && velocity30dAgo
      ? ((latestVelocity.velocity / velocity30dAgo.velocity - 1) * 100)
      : null;

    // Total supply info
    const latestSupply = totalSupply[totalSupply.length - 1]?.value || 0;

    // LTH supply estimate: coins NOT moved in 155+ days.
    // We estimate this from velocity: if X% of supply moves daily (7d avg),
    // then over 155 days, roughly (1 - (1-velocity/100)^155) of supply has moved.
    // LTH = total * (1 - velocity/100)^155
    const lthSupply = latestVelocity && latestSupply
      ? latestSupply * Math.pow(1 - latestVelocity.velocity / 100, 155)
      : null;
    const lthSupply30dAgo = velocity30dAgo && latestSupply
      ? latestSupply * Math.pow(1 - velocity30dAgo.velocity / 100, 155)
      : null;
    const lthChange = lthSupply && lthSupply30dAgo ? lthSupply - lthSupply30dAgo : null;

    // ATH drawdown
    const btcATH = btcHistory.length ? Math.max(...btcHistory.map(p => p.value || 0)) : null;
    const currentBtc = btcHistory.length ? btcHistory[btcHistory.length - 1]?.value : null;
    const athDrawdown = btcATH && currentBtc ? ((currentBtc / btcATH - 1) * 100) : null;

    // Wallets growth
    const latestWallets = walletsData.length ? walletsData[walletsData.length - 1]?.value : null;
    const wallets30dAgo = walletsData.length > 30 ? walletsData[walletsData.length - 31]?.value : null;
    const walletsChange = latestWallets && wallets30dAgo ? latestWallets - wallets30dAgo : null;

    // Chart data: velocity + BTC price (inverted relationship)
    const btcMap = new Map();
    for (const p of btcHistory) {
      if (p?.date) btcMap.set(`${p.date.getFullYear()}-${p.date.getMonth()}`, p.value);
    }

    const chartData = velocitySeries
      .filter((_, i) => i % 7 === 0) // weekly samples for chart clarity
      .map(p => {
        const btcPrice = btcMap.get(`${p.date.getFullYear()}-${p.date.getMonth()}`);
        return {
          date: format(p.date, 'MMM yy'),
          ts: p.date.getTime(),
          velocity: p.velocity,
          btcPrice: btcPrice ?? null,
        };
      });

    // 200-Week Moving Average
    const wma200 = weeklyBars.length >= 200
      ? weeklyBars.slice(-200).reduce((s, b) => s + b.close, 0) / 200
      : null;
    const wma200Premium = wma200 && btcSpot ? ((btcSpot / wma200 - 1) * 100) : null;
    // Monthly % increase of the 200WMA itself (shows long-term trend health)
    const wma200_4wAgo = weeklyBars.length >= 204
      ? weeklyBars.slice(-204, -4).slice(-200).reduce((s, b) => s + b.close, 0) / 200
      : null;
    const wma200MonthlyGrowth = wma200 && wma200_4wAgo ? ((wma200 / wma200_4wAgo - 1) * 100) : null;

    return {
      velocitySeries,
      latestVelocity,
      velocityTrend,
      latestSupply,
      lthSupply,
      lthChange,
      btcATH,
      athDrawdown,
      latestWallets,
      walletsChange,
      chartData,
      wma200,
      wma200Premium,
      wma200MonthlyGrowth,
    };
  }, [hodlData, walletsData, btcHistory, weeklyBars, btcSpot]);

  if (hodlData.status === 'loading') {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-20 rounded-xl bg-slate-900 border border-slate-800" />
        <div className="h-64 rounded-2xl bg-slate-900 border border-slate-800" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-300">HODLer data unavailable</h3>
            <p className="text-sm text-slate-400 mt-1">{hodlData.error || 'Could not load on-chain conviction metrics.'}</p>
            <button onClick={() => window.location.reload()}
                    className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-red-500/20 border border-red-500/40 text-red-300">
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { latestVelocity, velocityTrend, latestSupply, lthSupply, lthChange, btcATH, athDrawdown, latestWallets, walletsChange, chartData, wma200, wma200Premium, wma200MonthlyGrowth } = analysis;

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-amber-500/[0.03] via-slate-900/60 to-violet-500/[0.03] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -top-24 -left-24 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="relative space-y-5">

        <div>
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            Long-Term Holder Conviction
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Price is one signal. Ownership behavior is another — the people who have held Bitcoin longest are not selling into weakness.
          </p>
        </div>

        {/* 200-Week Moving Average — the "value zone" indicator */}
        {wma200 && (
          <div className={`rounded-xl border p-4 ${
            wma200Premium != null && wma200Premium <= 0
              ? 'border-emerald-500/40 bg-emerald-500/[0.08]'
              : wma200Premium != null && wma200Premium < 20
              ? 'border-amber-500/30 bg-amber-500/[0.05]'
              : 'border-white/10 bg-slate-900/40'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">200-Week Moving Average</div>
                <div className="flex items-baseline gap-3">
                  <span className="text-2xl font-bold font-mono tabular-nums text-slate-100">
                    ${wma200.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                  {wma200MonthlyGrowth != null && (
                    <span className="text-xs font-mono text-slate-500">
                      +{wma200MonthlyGrowth.toFixed(2)}%/mo
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  {wma200Premium != null && wma200Premium <= 0
                    ? 'Price is AT or BELOW the 200WMA — historically the strongest accumulation zone'
                    : wma200Premium != null && wma200Premium < 20
                    ? 'Price is near the 200WMA — approaching long-term value territory'
                    : 'Price is above the 200WMA — not in the historical buy zone yet'
                  }
                </p>
              </div>
              <div className="text-right">
                <div className={`text-xl font-bold font-mono tabular-nums ${
                  wma200Premium != null && wma200Premium <= 0 ? 'text-emerald-400'
                    : wma200Premium != null && wma200Premium < 20 ? 'text-amber-400'
                    : 'text-slate-300'
                }`}>
                  {wma200Premium != null ? `${wma200Premium >= 0 ? '+' : ''}${wma200Premium.toFixed(1)}%` : '—'}
                </div>
                <div className="text-[10px] text-slate-500">
                  {wma200Premium != null && wma200Premium <= 0 ? 'below 200WMA' : 'above 200WMA'}
                </div>
                {btcSpot && (
                  <div className="text-xs font-mono text-slate-400 mt-1">
                    BTC: ${btcSpot.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                )}
              </div>
            </div>
            {/* Visual distance gauge */}
            {wma200Premium != null && (
              <div className="mt-3">
                <div className="relative w-full h-2 bg-slate-800 rounded-full overflow-visible">
                  {/* Zone markers */}
                  <div className="absolute left-0 top-0 h-full w-[25%] bg-emerald-500/20 rounded-l-full" />
                  <div className="absolute left-[25%] top-0 h-full w-[15%] bg-amber-500/15" />
                  {/* Current position indicator */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-lg"
                    style={{
                      left: `${Math.min(95, Math.max(2, (wma200Premium + 30) / 130 * 100))}%`,
                      background: wma200Premium <= 0 ? '#34d399' : wma200Premium < 20 ? '#fbbf24' : '#94a3b8',
                    }}
                  />
                </div>
                <div className="flex justify-between text-[9px] text-slate-600 mt-1 font-mono">
                  <span>-30% (deep value)</span>
                  <span>0% (200WMA)</span>
                  <span>+100%</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Primary LTH metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ConvictionCard
            label="Est. LTH Supply"
            value={lthSupply ? `${(lthSupply / 1e6).toFixed(1)}M BTC` : '—'}
            sub={lthChange != null ? `${lthChange >= 0 ? '+' : ''}${(lthChange / 1000).toFixed(0)}K (30d)` : null}
            tone={lthChange != null ? (lthChange > 0 ? 'accumulating' : 'distributing') : 'neutral'}
            detail="Coins unmoved 155+ days (velocity-derived)"
          />
          <ConvictionCard
            label="ATH Drawdown"
            value={athDrawdown != null ? `${athDrawdown.toFixed(0)}%` : '—'}
            sub={btcATH ? `ATH: $${Math.round(btcATH).toLocaleString()}` : null}
            tone={athDrawdown != null ? (athDrawdown < -30 ? 'accumulating' : athDrawdown < -10 ? 'neutral' : 'neutral') : 'neutral'}
            detail={btcSpot ? `Now: $${Math.round(btcSpot).toLocaleString()}` : null}
          />
          <ConvictionCard
            label="Network Velocity"
            value={latestVelocity ? `${latestVelocity.velocity.toFixed(2)}%` : '—'}
            sub={velocityTrend != null ? `${velocityTrend >= 0 ? '↑' : '↓'} ${Math.abs(velocityTrend).toFixed(1)}% (30d)` : null}
            tone={velocityTrend != null ? (velocityTrend < -5 ? 'accumulating' : velocityTrend > 5 ? 'distributing' : 'neutral') : 'neutral'}
            detail="Lower = stronger holding conviction"
          />
          <ConvictionCard
            label="Accumulating Wallets"
            value={latestWallets ? latestWallets.toLocaleString() : '—'}
            sub={walletsChange != null ? `${walletsChange >= 0 ? '+' : ''}${walletsChange.toLocaleString()} (30d)` : null}
            tone={walletsChange != null ? (walletsChange > 0 ? 'accumulating' : 'distributing') : 'neutral'}
            detail="Addresses ≥0.01 BTC growing"
          />
        </div>

        {/* Divergence callout — when LTH supply rising while price falling */}
        {athDrawdown != null && athDrawdown < -15 && lthChange != null && lthChange > 0 && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3 flex items-start gap-3">
            <Shield className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-emerald-300 leading-relaxed">
              <strong>Price ↓ / Conviction ↑ Divergence:</strong> BTC is {Math.abs(athDrawdown).toFixed(0)}% below its all-time high
              while estimated long-term holder supply is <em>rising</em> (+{(lthChange / 1000).toFixed(0)}K BTC in 30 days).
              {walletsChange != null && walletsChange > 0 && ` Meanwhile ${walletsChange.toLocaleString()} new wallets accumulated ≥0.01 BTC.`}
              {' '}The supply held by long-term holders is approaching record highs — those who held longest are not selling.
              <span className="block mt-1 text-slate-400">Bitcoin's supply is capped at 21M. Fewer than 1M wallets hold a whole coin. At a lower price, each dollar buys more.</span>
            </div>
          </div>
        )}

        {/* Velocity vs Price chart */}
        {chartData.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              Velocity vs BTC Price — falling velocity = HODLer conviction rising
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} />
                  <YAxis
                    yAxisId="vel"
                    tick={{ fill: 'rgba(167,139,250,0.9)', fontSize: 11 }}
                    tickLine={false}
                    tickFormatter={(v) => `${v.toFixed(1)}%`}
                    reversed
                  />
                  <YAxis
                    yAxisId="price"
                    orientation="right"
                    tick={{ fill: 'rgba(247,147,26,0.9)', fontSize: 11 }}
                    tickLine={false}
                    tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
                  />
                  <Area yAxisId="vel" type="monotone" dataKey="velocity" stroke="none" fill="rgba(167,139,250,0.15)" />
                  <Line yAxisId="vel" type="monotone" dataKey="velocity" stroke="#a78bfa" strokeWidth={2} dot={false} name="Velocity (inverted)" />
                  <Line yAxisId="price" type="monotone" dataKey="btcPrice" stroke="#f7931a" strokeWidth={2} dot={false} name="BTC Price" connectNulls />
                  <Tooltip
                    cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
                    content={<VelocityTooltip />}
                  />
                  <Legend wrapperStyle={{ paddingTop: 8, fontSize: 11 }} iconType="line"
                          formatter={(v) => <span className="text-slate-300">{v}</span>} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-slate-600 mt-1">
              Y-axis inverted: lower velocity (top) = more coins being held. When velocity falls while price drops, it signals accumulation not capitulation.
            </p>
          </div>
        )}

        {/* Supply vs Headwinds context */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Supply Scarcity</div>
            <div className="space-y-2 text-xs text-slate-300">
              <div className="flex justify-between">
                <span className="text-slate-500">Max supply:</span>
                <span className="font-mono font-bold text-slate-100">21,000,000 BTC</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Mined:</span>
                <span className="font-mono font-bold text-slate-100">{latestSupply ? `${(latestSupply / 1e6).toFixed(2)}M` : '—'} <span className="text-slate-600">({latestSupply ? ((latestSupply / 21_000_000 * 100)).toFixed(1) : '—'}%)</span></span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Est. LTH held:</span>
                <span className="font-mono font-bold text-emerald-400">{lthSupply ? `~${(lthSupply / 1e6).toFixed(1)}M` : '—'} <span className="text-slate-600">({lthSupply && latestSupply ? ((lthSupply / latestSupply * 100)).toFixed(0) : '—'}%)</span></span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Whole-coin wallets:</span>
                <span className="font-mono font-bold text-slate-100">&lt; 1M</span>
              </div>
            </div>
          </div>
          <LiveHeadwinds fred={fred} equityHistory={equityHistory} btcHistory={btcHistory} btcSpot={btcSpot} />
        </div>
      </div>
    </section>
  );
}

function LiveHeadwinds({ fred, equityHistory, btcHistory, btcSpot }) {
  const headwinds = useMemo(() => {
    const out = [];
    const cpiYoY = fred.cpiYoY || [];
    const ffrSeries = fred.ffr || [];
    const dxySeries = equityHistory.dxy || [];
    const nasdaqSeries = equityHistory.nasdaq || [];

    // 1. Inflation + Fed rates
    const latestCpi = cpiYoY.length ? cpiYoY[cpiYoY.length - 1]?.value : null;
    const latestFfr = ffrSeries.length ? ffrSeries[ffrSeries.length - 1]?.value : null;
    const ffrActive = latestFfr != null && latestFfr > 3;
    out.push({
      label: 'Elevated Rates',
      active: ffrActive,
      value: latestFfr != null ? `FFR ${latestFfr.toFixed(2)}%` : null,
      detail: latestCpi != null ? `CPI ${latestCpi.toFixed(1)}% YoY — ${ffrActive ? 'rates still restrictive, suppressing risk appetite' : 'rates easing, headwind fading'}` : null,
    });

    // 2. Dollar strength
    const dxyLatest = dxySeries.length ? dxySeries[dxySeries.length - 1]?.value : null;
    const dxy12mAgo = dxySeries.length > 12 ? dxySeries[dxySeries.length - 13]?.value : null;
    const dxyYoY = dxyLatest && dxy12mAgo ? ((dxyLatest / dxy12mAgo - 1) * 100) : null;
    const dxyActive = dxyYoY != null && dxyYoY > 2;
    out.push({
      label: 'Strong Dollar',
      active: dxyActive,
      value: dxyYoY != null ? `DXY ${dxyYoY >= 0 ? '+' : ''}${dxyYoY.toFixed(1)}% YoY` : null,
      detail: dxyActive ? 'USD strength absorbing global liquidity signal — M2 expansion muted for BTC' : (dxyYoY != null ? 'Dollar weakening — liquidity transmission improving' : null),
    });

    // 3. Tech/AI outperformance pulling capital away from BTC
    const nasdaqLatest = nasdaqSeries.length ? nasdaqSeries[nasdaqSeries.length - 1]?.value : null;
    const nasdaq6mAgo = nasdaqSeries.length > 6 ? nasdaqSeries[nasdaqSeries.length - 7]?.value : null;
    const nasdaqRet = nasdaqLatest && nasdaq6mAgo ? ((nasdaqLatest / nasdaq6mAgo - 1) * 100) : null;
    const btcLatest = btcHistory.length ? btcHistory[btcHistory.length - 1]?.value : null;
    const btc6mAgo = btcHistory.length > 6 ? btcHistory[btcHistory.length - 7]?.value : null;
    const btcRet = btcLatest && btc6mAgo ? ((btcLatest / btc6mAgo - 1) * 100) : null;
    const rotationActive = nasdaqRet != null && btcRet != null && nasdaqRet > btcRet + 10;
    out.push({
      label: 'AI / Tech Rotation',
      active: rotationActive,
      value: nasdaqRet != null ? `Nasdaq ${nasdaqRet >= 0 ? '+' : ''}${nasdaqRet.toFixed(0)}% (6mo)` : null,
      detail: rotationActive
        ? `BTC ${btcRet >= 0 ? '+' : ''}${btcRet?.toFixed(0)}% same period — capital favoring tech over crypto`
        : (btcRet != null ? `BTC ${btcRet >= 0 ? '+' : ''}${btcRet.toFixed(0)}% (6mo) — rotation headwind fading` : null),
    });

    // 4. ETF demand proxy: price declining while velocity low = demand-side weakness
    const btc30dAgo = btcHistory.length > 4 ? btcHistory[btcHistory.length - 5]?.value : null;
    const btc30dRet = btcSpot && btc30dAgo ? ((btcSpot / btc30dAgo - 1) * 100) : null;
    const demandWeak = btc30dRet != null && btc30dRet < -5;
    out.push({
      label: 'Demand-Side Pressure',
      active: demandWeak,
      value: btc30dRet != null ? `BTC ${btc30dRet >= 0 ? '+' : ''}${btc30dRet.toFixed(1)}% (30d)` : null,
      detail: demandWeak
        ? 'Price declining despite low velocity — new buyer demand absent (ETF outflows likely)'
        : 'Price stable/rising — demand-side pressure not evident',
    });

    return out;
  }, [fred, equityHistory, btcHistory, btcSpot]);

  const activeCount = headwinds.filter(h => h.active).length;

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Live Headwinds</div>
        <span className={`text-[10px] font-bold font-mono ${
          activeCount >= 3 ? 'text-red-400' : activeCount >= 2 ? 'text-amber-400' : 'text-emerald-400'
        }`}>
          {activeCount}/{headwinds.length} active
        </span>
      </div>
      <div className="space-y-2.5">
        {headwinds.map((h, i) => (
          <div key={i} className={`flex items-start gap-2.5 ${h.active ? '' : 'opacity-50'}`}>
            <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${h.active ? 'bg-red-400' : 'bg-emerald-400'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-200">{h.label}</span>
                {h.value && (
                  <span className={`text-[10px] font-mono tabular-nums ${h.active ? 'text-red-400' : 'text-emerald-400'}`}>
                    {h.value}
                  </span>
                )}
              </div>
              {h.detail && <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{h.detail}</p>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-600 mt-3 border-t border-white/5 pt-2">
        Headwinds are data-derived from live feeds. When conditions flip (rates cut, DXY falls, tech underperforms), they turn green automatically.
      </p>
    </div>
  );
}

function VelocityTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/95 backdrop-blur-md px-4 py-3 shadow-xl text-xs font-mono tabular-nums">
      <div className="text-slate-200 font-semibold mb-2 font-sans">{row.date}</div>
      <div className="space-y-1">
        {row.velocity != null && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-violet-300 font-sans">Velocity:</span>
            <span className="text-slate-100">{row.velocity.toFixed(3)}%</span>
          </div>
        )}
        {row.btcPrice != null && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-orange-300 font-sans">BTC:</span>
            <span className="text-slate-100">${Math.round(row.btcPrice).toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ConvictionCard({ label, value, sub, tone, detail }) {
  const toneColors = {
    accumulating: 'text-emerald-400',
    distributing: 'text-red-400',
    neutral: 'text-slate-300',
  };
  return (
    <div className="px-3 py-2.5 bg-slate-900/40 border border-white/10 rounded-lg">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 truncate">{label}</div>
      <div className={`font-mono font-bold tabular-nums text-lg ${toneColors[tone] || toneColors.neutral}`}>{value}</div>
      {sub && <div className={`text-[10px] font-mono mt-0.5 ${toneColors[tone] || 'text-slate-500'}`}>{sub}</div>}
      {detail && <p className="text-[10px] text-slate-600 mt-1 leading-snug">{detail}</p>}
    </div>
  );
}

function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, d || 1);
}
